import {noop} from 'lodash';
import {FC, useMemo} from 'react';
import {connect} from 'react-redux';

import {isVisitorTeammate} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {buildPushV2EventDefinitions} from '../../../libs/shared/core/src/services/push/common/pushEventAdapter';
import {buildPushRequestContentRefreshMessage} from '../../../libs/shared/core/src/services/push/common/pushMessage';
import {PushEventDefinition, PushRenderTask} from '../../../libs/shared/core/src/services/push/pushTypes';
import {getPushConfig} from '../../../libs/shared/core/src/store/data/config/configSelectors';
import {ResolveStateProps} from '../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {usePushControllerContext} from '../../../libs/shared/ui/src/push/pushControllerContext';
import {usePushV2Events} from '../../../libs/shared/ui/src/push/usePushEvents';
import {useAppDispatch} from '../../../src/helpers/appReduxHelpers';
import {AppThunkDispatch} from '../../../src/store/appThunk';
import {getCompany, getIsNotificationCenterEnabled} from '../../../src/store/data/company/companySelectors';
import {pushConversationUpdatedAction} from '../../../src/store/data/conversations/pushConversationsActions';
import {getUser} from '../../../src/store/data/teammates/teammatesSelectors';
import {getCellsPanelsRooms} from '../../../src/store/panels/cells/cellsPanelSelectors';
import {getViewerPanelsConversationIds} from '../../../src/store/panels/viewer/viewerPanelSelectors';
import {makeMapStateToProps} from '../../helpers/reselectHelpers';
import {
  fetchUpdatedConversation,
  FetchUpdatedConversationOptions,
  makeFetchUpdatedConversationTasksSubject,
} from '../pushCommons';
import {PushEventSubscriptionsDelegate} from '../pushEventSubscriptions';
import {getConversationRoomName} from '../pushHelpers';
import {PushBaseProps} from '../pushTypes';

type PushEventSubscriptionsBuilder = (
  props: PushBaseProps,
  subscriptionsDelegate: PushEventSubscriptionsDelegate,
  dispatch: AppThunkDispatch,
) => ReadonlyArray<PushEventDefinition>;

interface PushGlobalEventHandlersProps extends ResolveStateProps<typeof mapStateToProps> {
  makePushEventSubscriptions: PushEventSubscriptionsBuilder;
}

export const PushGlobalEventHandlers: FC<PushGlobalEventHandlersProps> = ({
  pushConfig,
  company,
  user,
  cellsPanelsRooms,
  viewerPanelsConversationIds,
  isNotificationCenterEnabled,
  makePushEventSubscriptions,
}) => {
  const dispatch = useAppDispatch();
  const {pushController} = usePushControllerContext();
  const fetchUpdatedConversationTasks$ = useMemo(
    () =>
      makeFetchUpdatedConversationTasksSubject(async (options: FetchUpdatedConversationOptions) => {
        fetchUpdatedConversation(
          options,
          (conversationId, dataGroup, requesterId) => {
            dispatch(pushConversationUpdatedAction(conversationId, dataGroup, requesterId));
          },
          (task) => pushController.enqueueTask(task),
        );
      }),
    [dispatch, pushController],
  );

  const subscriptionsDelegate: PushEventSubscriptionsDelegate = useMemo(
    () => ({
      enqueueDelayedRenderTask: (renderTask: PushRenderTask, delay: number) => {
        pushController.enqueueDelayedRenderTask(renderTask, delay);
      },
      shouldDiscardServerPrecog: (conversationId: number, isPrecogAlreadySentDirectly: boolean) => {
        const isReceivingDirectPrecogs = pushController.isSubscribed(getConversationRoomName(conversationId));
        // We assume that we already received the direct precogs if they were sent by the other client and we're connected to the corresponding room.
        return isReceivingDirectPrecogs && isPrecogAlreadySentDirectly;
      },
      onFetchUpdatedConversation: (options: FetchUpdatedConversationOptions) => {
        fetchUpdatedConversationTasks$.next(options);
      },
      refreshContent: () => {
        pushController.dispatchSystemEvent(buildPushRequestContentRefreshMessage());
      },
      // Functionality directly implemented by DesktopFailedDraftBanner
      onDraftFailed: noop,
      // Functionality directly implemented by DesktopImpersonationRequestBanner
      onReceiveImpersonationRequest: noop,
    }),
    [fetchUpdatedConversationTasks$, pushController],
  );

  const definitions = makePushEventSubscriptions(
    {
      pusherConfiguration: pushConfig,
      company,
      user,
      cellsPanelsRooms,
      viewerPanelsConversationIds,
      isNotificationCenterEnabled,
      shouldAlwaysSubscribeToUserFolderRoom: !isVisitorTeammate(user),
    },
    subscriptionsDelegate,
    dispatch,
  );

  usePushV2Events(buildPushV2EventDefinitions(definitions));
  return null;
};

const mapStateToProps = makeMapStateToProps()(() => ({
  pushConfig: getPushConfig,
  company: getCompany,
  user: getUser,
  cellsPanelsRooms: getCellsPanelsRooms,
  viewerPanelsConversationIds: getViewerPanelsConversationIds,
  isNotificationCenterEnabled: getIsNotificationCenterEnabled,
}));

export const PushGlobalEventHandlersContainer = connect(mapStateToProps)(PushGlobalEventHandlers);
